import type {
  MaterialMasterDataSnapshot,
  MaterialMasterRecord,
  MaterialMasterSuggestion,
  MaterialMasterVersionInfo,
  MaterialMasterAvailableChanges,
} from "../types/materialMasterData";
import { formatStorageAddressDisplay, normalizeStorageAddress } from "../utils/storageAddress";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { isLocalBootstrapVersion } from "../utils/masterDataVersion";

interface RawMaterialMasterRecord {
  materialNummer?: unknown;
  bezeichnung?: unknown;
  typ?: unknown;
  hersteller?: unknown;
  lagerort?: unknown;
}

interface ApiMaterialRecord {
  material_number: string;
  description: string;
  material_type: string;
  manufacturer: string;
  storage_locations: string[];
  manual_state?: "created" | "updated" | null;
}

interface ApiVersion {
  id: number;
  version_number: number;
  source_filename: string | null;
  source_hash: string;
  source_date: string | null;
  activated_at: string | null;
}

interface ApiSnapshot {
  version: ApiVersion;
  materials: ApiMaterialRecord[];
  manual_revision?: string | null;
}

interface ApiStatus {
  available: boolean;
  version: ApiVersion | null;
  manual_revision?: string | null;
}

interface StoredMaterialMasterData {
  key: "current";
  records: MaterialMasterRecord[];
  createdAt: string | null;
  version: MaterialMasterVersionInfo;
  savedAt: string;
}

const LEGACY_MATERIAL_MASTER_URL = "/data/materialstammliste.json";
const SERVER_SNAPSHOT_URL = "/api/material-master/snapshot";
const SERVER_STATUS_URL = "/api/material-master/status";
const ANNOUNCED_VERSION_KEY = "materialentnahme:material-master:last-announced-version";
const DATABASE_NAME = "materialentnahme-master-data";
const DATABASE_VERSION = 2;
const STORE_NAME = "material-master";
const STORE_KEY = "current";

let memorySnapshot: MaterialMasterDataSnapshot | null = null;
let loadingPromise: Promise<MaterialMasterDataSnapshot> | null = null;
let bootstrapRefreshPromise: Promise<MaterialMasterDataSnapshot | null> | null = null;
let materialIndex: Map<string, MaterialMasterRecord> | null = null;

function emptyVersion(): MaterialMasterVersionInfo {
  return { id: null, versionNumber: 0, sourceFilename: null, sourceHash: null, sourceDate: null, activatedAt: null, manualRevision: null };
}

function asText(value: unknown): string { return typeof value === "string" ? value.trim() : ""; }
function normalizeText(value: string): string {
  return value.normalize("NFKC").toUpperCase().replace(/[‐‑‒–—−]/g, "-").replace(/[^A-Z0-9ÄÖÜẞ]+/g, " ").replace(/\s+/g, " ").trim();
}

function similarity(a: string, b: string): number {
  const left = normalizeText(a); const right = normalizeText(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  const rows = left.length + 1; const cols = right.length + 1;
  const matrix = Array.from({ length: rows }, () => new Array<number>(cols).fill(0));
  for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
  for (let j = 0; j < cols; j += 1) matrix[0][j] = j;
  for (let i = 1; i < rows; i += 1) for (let j = 1; j < cols; j += 1) {
    const cost = left[i - 1] === right[j - 1] ? 0 : 1;
    matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
  }
  return Math.max(0, 1 - matrix[left.length][right.length] / Math.max(left.length, right.length));
}

function looksLikeCreationDate(value: string): boolean { return /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2})?$/.test(value); }
function normalizeCreationDate(value: string): string | null {
  if (!looksLikeCreationDate(value)) return null;
  const date = new Date(value.replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}
function splitStorageLocations(rawValue: unknown): string[] {
  if (typeof rawValue !== "string") return [];
  return Array.from(new Set(rawValue.split(/[;,|]/g).map((part) => normalizeStorageAddress(part)).filter(Boolean)));
}

function parseLegacy(input: unknown): Omit<MaterialMasterDataSnapshot, "source" | "savedAt"> {
  if (!Array.isArray(input)) throw new Error("Die Materialstammliste hat kein gültiges Array-Format.");
  const records: MaterialMasterRecord[] = []; let createdAt: string | null = null;
  for (const entry of input) {
    if (typeof entry !== "object" || entry === null) continue;
    const raw = entry as RawMaterialMasterRecord;
    const materialNumber = asText(raw.materialNummer); const description = asText(raw.bezeichnung);
    const type = asText(raw.typ); const manufacturer = asText(raw.hersteller);
    if (materialNumber && !description && !type && !manufacturer && looksLikeCreationDate(materialNumber)) { createdAt = normalizeCreationDate(materialNumber); continue; }
    if (!/^983\d{7}$/.test(materialNumber)) continue;
    records.push({ materialNumber, description, type, manufacturer, storageLocations: splitStorageLocations(raw.lagerort) });
  }
  if (records.length === 0) throw new Error("Die Materialstammliste enthält keine gültigen Materialien.");
  return { records, createdAt, version: { ...emptyVersion(), sourceDate: createdAt } };
}

function mapApiRecord(record: ApiMaterialRecord): MaterialMasterRecord {
  return { materialNumber: record.material_number, description: record.description, type: record.material_type, manufacturer: record.manufacturer, storageLocations: (record.storage_locations ?? []).map(formatStorageAddressDisplay), manualState: record.manual_state ?? null };
}
function mapApiVersion(version: ApiVersion, manualRevision: string | null = null): MaterialMasterVersionInfo {
  return { id: version.id, versionNumber: version.version_number, sourceFilename: version.source_filename, sourceHash: version.source_hash, sourceDate: version.source_date, activatedAt: version.activated_at, manualRevision };
}
function buildIndex(snapshot: MaterialMasterDataSnapshot): void { materialIndex = new Map(snapshot.records.map((record) => [record.materialNumber, record])); }

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB konnte nicht geöffnet werden."));
  });
}

async function readStoredSnapshot(): Promise<MaterialMasterDataSnapshot | null> {
  if (!("indexedDB" in globalThis)) return null;
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(STORE_KEY);
      request.onsuccess = () => {
        const value = request.result as StoredMaterialMasterData | undefined;
        if (!value || !Array.isArray(value.records)) return resolve(null);
        resolve({ records: value.records, createdAt: value.createdAt, source: "indexed-db", version: value.version ?? emptyVersion(), savedAt: value.savedAt ?? null });
      };
      request.onerror = () => reject(request.error ?? new Error("Materialstammliste konnte nicht gelesen werden."));
    });
  } finally { database.close(); }
}

async function storeSnapshot(snapshot: MaterialMasterDataSnapshot): Promise<void> {
  if (!("indexedDB" in globalThis)) return;
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put({ key: STORE_KEY, records: snapshot.records, createdAt: snapshot.createdAt, version: snapshot.version, savedAt: new Date().toISOString() } satisfies StoredMaterialMasterData);
      tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error ?? new Error("Materialstammliste konnte nicht gespeichert werden."));
      tx.onabort = () => reject(tx.error ?? new Error("Speichern der Materialstammliste wurde abgebrochen."));
    });
  } finally { database.close(); }
}

async function fetchServerSnapshot(): Promise<MaterialMasterDataSnapshot> {
  const response = await fetchWithTimeout(SERVER_SNAPSHOT_URL, { credentials: "include", cache: "no-store", headers: { Accept: "application/json" } }, 12000);
  if (!response.ok) throw new Error(`Server-Materialstammliste konnte nicht geladen werden (${response.status}).`);
  const data = await response.json() as ApiSnapshot;
  const version = mapApiVersion(data.version, data.manual_revision ?? null);
  return { records: data.materials.map(mapApiRecord), createdAt: version.sourceDate, source: "server", version, savedAt: new Date().toISOString() };
}

async function fetchLegacySnapshot(): Promise<MaterialMasterDataSnapshot> {
  const response = await fetch(LEGACY_MATERIAL_MASTER_URL, { cache: "no-cache", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Lokale Materialstammliste konnte nicht geladen werden (${response.status}).`);
  return { ...parseLegacy(await response.json()), source: "legacy-network", savedAt: new Date().toISOString() };
}

function scheduleBootstrapConvergence(snapshot: MaterialMasterDataSnapshot): void {
  if (!isLocalBootstrapVersion(snapshot.version) || bootstrapRefreshPromise) return;
  bootstrapRefreshPromise = fetchServerSnapshot()
    .then(async (fresh) => {
      memorySnapshot = fresh;
      buildIndex(fresh);
      await storeSnapshot(fresh).catch(() => undefined);
      window.dispatchEvent(new Event("materialentnahme:master-data-updated"));
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
      return fresh;
    })
    .catch(() => null)
    .finally(() => { bootstrapRefreshPromise = null; });
}

async function loadSnapshotInternal(): Promise<MaterialMasterDataSnapshot> {
  const stored = await readStoredSnapshot().catch(() => null);

  if (stored) {
    memorySnapshot = stored;
    buildIndex(stored);

    // Version 0 (und der kompatible alte null-Bootstrap aus v0.148.0) ist kein
    // regulärer Serverstand. Sobald der Server erreichbar ist, konvergiert die
    // PWA automatisch und still auf dessen freigegebenen Snapshot. Erst echte
    // versionierte lokale Serverstände dürfen ein Update-Banner auslösen.
    if (isLocalBootstrapVersion(stored.version)) {
      scheduleBootstrapConvergence(stored);
    } else {
      void getMaterialMasterServerStatus()
        .then(async (server) => {
          if (!server) return;
          const sameVersion = server.versionNumber === stored.version.versionNumber;
          const sameRevision = (server.manualRevision ?? null) === (stored.version.manualRevision ?? null);
          if (!sameVersion || !sameRevision) {
            window.dispatchEvent(new Event("materialentnahme:master-data-status-updated"));
            return;
          }
          const fresh = await fetchServerSnapshot();
          memorySnapshot = fresh;
          buildIndex(fresh);
          await storeSnapshot(fresh).catch(() => undefined);
        })
        .catch(() => undefined);
    }

    return stored;
  }

  try {
    const server = await fetchServerSnapshot();
    memorySnapshot = server;
    buildIndex(server);
    await storeSnapshot(server).catch(() => undefined);
    return server;
  } catch (serverError) {
    try {
      const legacy = await fetchLegacySnapshot();
      memorySnapshot = legacy;
      buildIndex(legacy);
      await storeSnapshot(legacy).catch(() => undefined);
      return legacy;
    } catch {
      throw serverError;
    }
  }
}

export function loadMaterialMasterData(): Promise<MaterialMasterDataSnapshot> {
  if (memorySnapshot) {
    scheduleBootstrapConvergence(memorySnapshot);
    return Promise.resolve(memorySnapshot);
  }
  if (!loadingPromise) loadingPromise = loadSnapshotInternal().finally(() => { loadingPromise = null; });
  return loadingPromise;
}

export function getLoadedMaterialMasterData(): MaterialMasterDataSnapshot | null { return memorySnapshot; }

export async function findMaterialByNumber(materialNumber: string): Promise<{ record: MaterialMasterRecord | null; snapshot: MaterialMasterDataSnapshot }> {
  const snapshot = await loadMaterialMasterData(); if (!materialIndex) buildIndex(snapshot);
  return { record: materialIndex?.get(materialNumber.trim()) ?? null, snapshot };
}

export async function searchMaterialMasterData(query: string, limit = 100, offset = 0): Promise<{ records: MaterialMasterRecord[]; total: number; snapshot: MaterialMasterDataSnapshot }> {
  const snapshot = await loadMaterialMasterData();
  const normalized = normalizeText(query);
  if (!normalized) return { records: snapshot.records.slice(offset, offset + limit), total: snapshot.records.length, snapshot };
  const tokens = normalized.split(" ").filter(Boolean);
  const scored = snapshot.records
    .map((record) => {
      const number = record.materialNumber.toUpperCase();
      const haystack = normalizeText([record.materialNumber, record.description, record.type, record.manufacturer, ...record.storageLocations].join(" "));
      if (!tokens.every((token) => haystack.includes(token))) return null;
      let score = 0;
      if (number === normalized) score += 100;
      else if (number.startsWith(normalized)) score += 70;
      if (normalizeText(record.description).startsWith(normalized)) score += 25;
      if (normalizeText(record.type).startsWith(normalized)) score += 20;
      return { record, score };
    })
    .filter((item): item is { record: MaterialMasterRecord; score: number } => item !== null)
    .sort((a, b) => b.score - a.score || a.record.materialNumber.localeCompare(b.record.materialNumber));
  return { records: scored.slice(offset, offset + limit).map((item) => item.record), total: scored.length, snapshot };
}

export async function findMaterialSuggestions(
  materialNumber: string,
  materialText: string,
  materialType: string,
  storageAddress: string,
  limit = 10,
  offset = 0
): Promise<{ suggestions: MaterialMasterSuggestion[]; total: number; snapshot: MaterialMasterDataSnapshot }> {
  const snapshot = await loadMaterialMasterData();
  const numberQuery = materialNumber.replace(/\s+/g, "").toUpperCase();
  const textQuery = materialText.trim();
  const typeQuery = materialType.trim();
  const normalizedStorage = normalizeStorageAddress(storageAddress);
  const hasInput = Boolean(numberQuery || textQuery || typeQuery || normalizedStorage);

  if (!hasInput) return { suggestions: [], total: 0, snapshot };

  const ranked = snapshot.records
    .map((record): MaterialMasterSuggestion & { numberScore: number; directHit: boolean } => {
      let numberScore = 0;
      if (numberQuery) {
        if (record.materialNumber === numberQuery) numberScore = 1;
        else if (record.materialNumber.startsWith(numberQuery)) numberScore = 0.95;
        else if (record.materialNumber.includes(numberQuery)) numberScore = 0.82;
        else numberScore = similarity(numberQuery, record.materialNumber);
      }

      const typeScore = typeQuery ? similarity(typeQuery, record.type) : 0;
      const textScore = textQuery ? similarity(textQuery, record.description) : 0;
      const storageText = record.storageLocations.join(" ");
      const storageScore = normalizedStorage
        ? record.storageLocations.some((item) => item.includes(normalizedStorage))
          ? 1
          : similarity(normalizedStorage, storageText)
        : 0;

      // Filled fields share the weight. Material number has the strongest
      // signal because even a single digit should already rank candidates.
      const weights = [
        numberQuery ? [numberScore, 0.45] : null,
        textQuery ? [textScore, 0.30] : null,
        typeQuery ? [typeScore, 0.20] : null,
        normalizedStorage ? [storageScore, 0.15] : null,
      ].filter((item): item is [number, number] => item !== null);
      const totalWeight = weights.reduce((sum, item) => sum + item[1], 0);
      const score = totalWeight > 0
        ? weights.reduce((sum, item) => sum + item[0] * item[1], 0) / totalWeight
        : 0;
      const directHit = Boolean(
        (numberQuery && record.materialNumber.includes(numberQuery))
        || (textQuery && normalizeText(record.description).includes(normalizeText(textQuery)))
        || (typeQuery && normalizeText(record.type).includes(normalizeText(typeQuery)))
        || (normalizedStorage && record.storageLocations.some((item) => item.includes(normalizedStorage)))
      );

      return { record, numberScore, typeScore, textScore, storageScore, score, directHit };
    })
    .filter((item) => {
      // Im Fuzzy-Sheet sollen nur echte Treffer erscheinen. Bei sehr kurzen
      // Eingaben reicht deshalb kein zufälliger Ähnlichkeitsscore aus.
      const shortestQueryLength = Math.min(
        ...[numberQuery, textQuery, typeQuery, normalizedStorage]
          .filter(Boolean)
          .map((value) => value.length)
      );
      const threshold = shortestQueryLength <= 2 ? 0.72 : shortestQueryLength <= 4 ? 0.58 : 0.46;
      return item.directHit || item.score >= threshold;
    })
    .sort((a, b) => Number(b.directHit) - Number(a.directHit) || b.score - a.score || a.record.materialNumber.localeCompare(b.record.materialNumber));

  return {
    suggestions: ranked.slice(offset, offset + Math.max(1, limit)).map((item) => ({ record: item.record, score: item.score, typeScore: item.typeScore, textScore: item.textScore, storageScore: item.storageScore })),
    total: ranked.length,
    snapshot,
  };
}

export async function refreshMaterialMasterData(): Promise<MaterialMasterDataSnapshot> {
  const fresh = await fetchServerSnapshot(); memorySnapshot = fresh; buildIndex(fresh); await storeSnapshot(fresh).catch(() => undefined); return fresh;
}


export async function getMaterialMasterServerStatus(): Promise<MaterialMasterVersionInfo | null> {
  const response = await fetchWithTimeout(SERVER_STATUS_URL, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }, 5000);
  if (!response.ok) {
    throw new Error(`Materialstamm-Status konnte nicht geprüft werden (${response.status}).`);
  }
  const data = await response.json() as ApiStatus;
  return data.available && data.version ? mapApiVersion(data.version, data.manual_revision ?? null) : null;
}

export async function getMaterialMasterAvailableChanges(sinceVersion: number, sinceRevision: string | null): Promise<MaterialMasterAvailableChanges> {
  const params = new URLSearchParams({ since_version: String(Math.max(0, sinceVersion)) });
  if (sinceRevision) params.set("since_revision", sinceRevision);
  const response = await fetch(`/api/material-master/changes?${params.toString()}`, { credentials: "include", cache: "no-store", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Änderungen konnten nicht geladen werden (${response.status}).`);
  const data = await response.json() as { version_number: number | null; manual_revision: string | null; new_entries: number; changed_entries: number; removed_entries: number; changes: Array<{ key: string; change_type: string; old_data: Record<string, unknown> | null; new_data: Record<string, unknown> | null; changed_at: string | null }> };
  return {
    versionNumber: data.version_number,
    manualRevision: data.manual_revision,
    newEntries: data.new_entries,
    changedEntries: data.changed_entries,
    removedEntries: data.removed_entries,
    changes: data.changes.map((item) => ({ key: item.key, changeType: item.change_type, oldData: item.old_data, newData: item.new_data, changedAt: item.changed_at })),
  };
}

export function getLastAnnouncedMaterialMasterVersion(): number {
  try {
    return Number(window.localStorage.getItem(ANNOUNCED_VERSION_KEY) ?? "0");
  } catch {
    return 0;
  }
}

export function wasMaterialMasterVersionAnnounced(versionNumber: number): boolean {
  return getLastAnnouncedMaterialMasterVersion() >= versionNumber;
}

export function markMaterialMasterVersionAnnounced(versionNumber: number): void {
  try {
    window.localStorage.setItem(ANNOUNCED_VERSION_KEY, String(versionNumber));
  } catch {
    // localStorage kann in restriktiven Browsermodi gesperrt sein.
  }
}

export async function getCachedMaterialMasterData(): Promise<MaterialMasterDataSnapshot | null> {
  if (memorySnapshot) {
    scheduleBootstrapConvergence(memorySnapshot);
    return memorySnapshot;
  }
  const stored = await readStoredSnapshot().catch(() => null);
  if (stored) {
    memorySnapshot = stored;
    buildIndex(stored);
    scheduleBootstrapConvergence(stored);
  }
  return stored;
}
